import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useAuth } from '../contexts/AuthContext';

interface LockdownConfig {
    enabled: boolean;
    disableCopy: boolean;
    disablePrint: boolean;
    disableContextMenu: boolean;
    disableSaveShortcut: boolean;
    disableDevToolsShortcut: boolean;
    watermark: boolean;
}
interface AutoProvisionConfig {
    enabled: boolean;
    allowedDomains: string[];
}
interface OrgConfig {
    githubOrg: string;
    installationId: number | null;
    defaultIssueLabels: string[];
    defaultSensitiveContentLock?: boolean;
    lockdown?: LockdownConfig;
    autoProvision?: AutoProvisionConfig;
    updatedAt: string;
    updatedBy: string;
}

const DEFAULT_LOCKDOWN: LockdownConfig = {
    enabled: false,
    disableCopy: true,
    disablePrint: true,
    disableContextMenu: true,
    disableSaveShortcut: true,
    disableDevToolsShortcut: true,
    watermark: true
};

const DEFAULT_AUTO_PROVISION: AutoProvisionConfig = {
    enabled: false,
    allowedDomains: []
};

export function AdminOrgPage() {
    const { tokenReady } = useAuth();
    const [cfg, setCfg] = useState<OrgConfig | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState<Partial<OrgConfig>>({});
    const [savedAt, setSavedAt] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await apiCall<OrgConfig>('/manage/org-config');
            setCfg(r);
            setDraft({});
            setError(null);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        }
    }, []);

    useEffect(() => {
        if (tokenReady) void load();
    }, [tokenReady, load]);

    async function save() {
        if (!cfg) return;
        setSaving(true);
        setError(null);
        try {
            const body: Partial<OrgConfig> = {
                ...draft,
                ...(draft.defaultIssueLabels !== undefined
                    ? {
                          defaultIssueLabels: (draft.defaultIssueLabels as unknown as string)
                              .toString()
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean)
                      }
                    : {})
            };
            const r = await apiCall<OrgConfig>('/manage/org-config', { method: 'PATCH', body });
            setCfg(r);
            setDraft({});
            setSavedAt(new Date().toLocaleTimeString());
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSaving(false);
        }
    }

    if (!cfg) {
        return <div className="admin-page">{error ?? 'Loading…'}</div>;
    }

    const orgValue = draft.githubOrg ?? cfg.githubOrg;
    const idValue = draft.installationId ?? cfg.installationId ?? '';
    const labelsValue =
        draft.defaultIssueLabels !== undefined
            ? (draft.defaultIssueLabels as unknown as string)
            : (cfg.defaultIssueLabels ?? []).join(', ');
    const dirty = Object.keys(draft).length > 0;
    const installUrl =
        cfg.githubOrg && cfg.installationId
            ? `https://github.com/organizations/${encodeURIComponent(cfg.githubOrg)}/settings/installations/${cfg.installationId}`
            : null;

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div>
                    <h1>Organization</h1>
                    <p className="admin-page__sub">
                        GitHub org settings. The App's private key (already in Key Vault) authenticates as the install
                        — no personal GitHub login is required at runtime.
                    </p>
                </div>
            </header>

            {error && <div className="admin-page__error">{error}</div>}
            {savedAt && !error && <div className="form-success">Saved at {savedAt}</div>}

            <div className="form-section">
                <label className="form-group">
                    <span className="form-label">GitHub org slug</span>
                    <input
                        type="text"
                        value={orgValue}
                        placeholder="Bay-Area-Community-Resources"
                        onChange={(e) => setDraft((d) => ({ ...d, githubOrg: e.target.value }))}
                        disabled={saving}
                    />
                </label>
                <label className="form-group">
                    <span className="form-label">Installation ID</span>
                    <input
                        type="number"
                        value={idValue as number | string}
                        placeholder="e.g. 56789012"
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                installationId: e.target.value === '' ? null : Number(e.target.value)
                            }))
                        }
                        disabled={saving}
                    />
                    <span className="form-hint">
                        Find it under GitHub → org settings → Installed GitHub Apps → Configure → URL ends in the
                        installation ID.
                    </span>
                </label>
                <label className="form-group">
                    <span className="form-label">Default issue labels (comma-separated)</span>
                    <input
                        type="text"
                        value={labelsValue}
                        placeholder="needs-triage, internal"
                        onChange={(e) => setDraft((d) => ({ ...d, defaultIssueLabels: e.target.value as unknown as string[] }))}
                        disabled={saving}
                    />
                </label>
                <div className="comment-composer__actions">
                    <button className="btn btn--primary" onClick={() => void save()} disabled={saving || !dirty}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>

            {installUrl && (
                <div className="form-section">
                    <h3>Manage install on GitHub</h3>
                    <p className="form-hint">
                        Open GitHub to add/remove repos from this installation. When the install scope changes, the
                        repos appear (or disappear) in <strong>Admin → Repos → Discover</strong>.
                    </p>
                    <a className="btn btn--ghost" href={installUrl} target="_blank" rel="noreferrer noopener">
                        Open installation in GitHub ↗
                    </a>
                </div>
            )}

            <AutoProvisionSection
                cfg={cfg}
                setDraft={setDraft}
                draftAutoProvision={draft.autoProvision}
                saving={saving}
            />

            <LockdownSection cfg={cfg} onSave={save} setDraft={setDraft} draftLockdown={draft.lockdown} saving={saving} />

            <p className="form-hint">
                Last updated by {cfg.updatedBy} at {new Date(cfg.updatedAt).toLocaleString()}.
            </p>
        </div>
    );
}

function AutoProvisionSection({
    cfg,
    setDraft,
    draftAutoProvision,
    saving
}: {
    cfg: OrgConfig;
    setDraft: React.Dispatch<React.SetStateAction<Partial<OrgConfig>>>;
    draftAutoProvision: AutoProvisionConfig | undefined;
    saving: boolean;
}) {
    const effective: AutoProvisionConfig = {
        ...DEFAULT_AUTO_PROVISION,
        ...(cfg.autoProvision ?? {}),
        ...(draftAutoProvision ?? {})
    };
    const [domainsText, setDomainsText] = useState<string | null>(null);
    const displayText = domainsText ?? effective.allowedDomains.join(', ');

    function patch(part: Partial<AutoProvisionConfig>) {
        setDraft((d) => ({ ...d, autoProvision: { ...effective, ...part } }));
    }

    function onDomainsChange(text: string) {
        setDomainsText(text);
        const list = text
            .split(/[,\s]+/)
            .map((s) => s.trim().toLowerCase().replace(/^@/, ''))
            .filter(Boolean);
        const dedup: string[] = [];
        for (const d of list) if (!dedup.includes(d)) dedup.push(d);
        patch({ allowedDomains: dedup });
    }

    return (
        <div className="form-section">
            <h3>Sign-up (auto-provision)</h3>
            <p className="form-hint">
                When enabled, any user whose verified email ends in one of the listed domains becomes an active
                reviewer on first sign-in — no admin invite needed. The new reviewer starts with <strong>no repo
                access</strong>; an admin still has to assign roles or grants before they can read any repo.
            </p>
            <p className="form-hint">
                Verified emails come from your identity provider (Google or BACR Workforce) via Microsoft External
                ID. To prevent self-signup leakage, list only domains you control or trust.
            </p>
            <label className="form-group form-group--inline">
                <input
                    type="checkbox"
                    checked={effective.enabled}
                    onChange={(e) => patch({ enabled: e.target.checked })}
                    disabled={saving}
                />
                <span><strong>Enable auto-provisioning</strong> for the domains below</span>
            </label>
            <label className="form-group">
                <span className="form-label">Allowed domains (comma- or space-separated)</span>
                <input
                    type="text"
                    value={displayText}
                    placeholder="bacr.org, example.org"
                    onChange={(e) => onDomainsChange(e.target.value)}
                    disabled={saving}
                />
                <span className="form-hint">
                    Lowercase domain only (no <code>@</code>). Match is exact on the part after the <code>@</code>;
                    subdomains are not implied (<code>bacr.org</code> does <em>not</em> match
                    <code> staff.bacr.org</code>).
                </span>
            </label>
        </div>
    );
}

function LockdownSection({
    cfg,
    setDraft,
    draftLockdown,
    saving
}: {
    cfg: OrgConfig;
    onSave: () => Promise<void>;
    setDraft: React.Dispatch<React.SetStateAction<Partial<OrgConfig>>>;
    draftLockdown: LockdownConfig | undefined;
    saving: boolean;
}) {
    const effective: LockdownConfig = { ...DEFAULT_LOCKDOWN, ...(cfg.lockdown ?? {}), ...(draftLockdown ?? {}) };
    function patch(part: Partial<LockdownConfig>) {
        setDraft((d) => ({ ...d, lockdown: { ...effective, ...part } }));
    }
    return (
        <div className="form-section">
            <h3>Reader lockdown (DRM-lite)</h3>
            <p className="form-hint">
                Best-effort browser deterrents. <strong>Cannot</strong> block OS screenshots, phone photos, or browser
                extensions — the watermark is the only mitigation for those. Power users can still bypass keyboard
                blocks via browser menus.
            </p>
            <label className="form-group form-group--inline">
                <input
                    type="checkbox"
                    checked={effective.enabled}
                    onChange={(e) => patch({ enabled: e.target.checked })}
                    disabled={saving}
                />
                <span><strong>Enable lockdown mode</strong> for all reviewers in this tenant</span>
            </label>
            <div className={'lockdown-options' + (effective.enabled ? '' : ' lockdown-options--disabled')}>
                <label className="form-group form-group--inline">
                    <input type="checkbox" checked={effective.disableCopy} onChange={(e) => patch({ disableCopy: e.target.checked })} disabled={saving || !effective.enabled} />
                    <span>Block Copy / Cut (Ctrl+C, Ctrl+X, right-click → Copy)</span>
                </label>
                <label className="form-group form-group--inline">
                    <input type="checkbox" checked={effective.disablePrint} onChange={(e) => patch({ disablePrint: e.target.checked })} disabled={saving || !effective.enabled} />
                    <span>Block Print (Ctrl+P; doc blanked in print stylesheet)</span>
                </label>
                <label className="form-group form-group--inline">
                    <input type="checkbox" checked={effective.disableContextMenu} onChange={(e) => patch({ disableContextMenu: e.target.checked })} disabled={saving || !effective.enabled} />
                    <span>Block right-click context menu</span>
                </label>
                <label className="form-group form-group--inline">
                    <input type="checkbox" checked={effective.disableSaveShortcut} onChange={(e) => patch({ disableSaveShortcut: e.target.checked })} disabled={saving || !effective.enabled} />
                    <span>Block Save As (Ctrl+S)</span>
                </label>
                <label className="form-group form-group--inline">
                    <input type="checkbox" checked={effective.disableDevToolsShortcut} onChange={(e) => patch({ disableDevToolsShortcut: e.target.checked })} disabled={saving || !effective.enabled} />
                    <span>Block DevTools shortcuts (F12, Ctrl+Shift+I/J/C, Ctrl+U) — bypass via browser menu still possible</span>
                </label>
                <label className="form-group form-group--inline">
                    <input type="checkbox" checked={effective.watermark} onChange={(e) => patch({ watermark: e.target.checked })} disabled={saving || !effective.enabled} />
                    <span>Watermark every page with reviewer email + timestamp (attribution for any captured image)</span>
                </label>
            </div>
        </div>
    );
}
